import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ArrowLeft, ScrollText } from 'lucide-react';
import type { RootState } from '../../../../../redux/store';
import { formatDate, today } from '../forms/FormParts';
import '../../shared-modules.css';
import '../forms/FinanceForms.css';
import '../QuickActionsHub.css';
import '../../FixedAssetsView/FixedAssetsView.css';

// Partida de la cuenta de un deudor o de un acreedor
interface AccountItem {
  id: string;
  status: 'abierta' | 'compensada';
  reference: string;
  invoice: string;
  documentDate: string;
  dueDate: string;
  paymentMethod: string;
  specialIndicator: string;
  documentType: string;
  amount: number;
  documentCurrency: string;
  documentNumber: string;
  text: string;
  clearingDocument: string;
  clearingDate: string;
  taxCode: string;
}

type AccountKind = 'deudor' | 'acreedor';

type ItemScope = 'abiertas' | 'todas';

type ColumnId =
  | 'status' | 'company' | 'account' | 'reference' | 'invoice' | 'documentDate' | 'dueDate'
  | 'paymentMethod' | 'specialIndicator' | 'documentType' | 'due' | 'amount' | 'currency'
  | 'documentCurrency' | 'documentNumber' | 'text' | 'clearingDocument' | 'clearingDate' | 'taxCode';

interface ItemColumn {
  id: ColumnId;
  header: string;
  title?: string;
  right?: boolean;
}

const COLUMN: Record<ColumnId, ItemColumn> = {
  status: { id: 'status', header: 'ST', title: 'Estado' },
  company: { id: 'company', header: 'SOC.', title: 'Sociedad' },
  account: { id: 'account', header: 'CUENTA', title: 'Cuenta del deudor' },
  reference: { id: 'reference', header: 'REFERENCIA' },
  invoice: { id: 'invoice', header: 'FACTURA' },
  documentDate: { id: 'documentDate', header: 'FECHA DOC.', title: 'Fecha del documento' },
  dueDate: { id: 'dueDate', header: 'VENC. NETO', title: 'Vencimiento neto' },
  paymentMethod: { id: 'paymentMethod', header: 'VP', title: 'Vía de pago' },
  specialIndicator: { id: 'specialIndicator', header: 'I', title: 'Indicador CME (operaciones especiales: anticipos, fianzas, efectos)' },
  documentType: { id: 'documentType', header: 'CL.', title: 'Clase de documento' },
  due: { id: 'due', header: 'VE', title: 'Vencimiento' },
  amount: { id: 'amount', header: 'IMPTE. ML', title: 'Importe en moneda local', right: true },
  currency: { id: 'currency', header: 'ML', title: 'Moneda local' },
  documentCurrency: { id: 'documentCurrency', header: 'MON.', title: 'Moneda del documento' },
  documentNumber: { id: 'documentNumber', header: 'Nº DOC.', title: 'Número de documento' },
  text: { id: 'text', header: 'TEXTO' },
  clearingDocument: { id: 'clearingDocument', header: 'DOC. COMP.', title: 'Documento de compensación' },
  clearingDate: { id: 'clearingDate', header: 'COMPENS.', title: 'Fecha de compensación' },
  taxCode: { id: 'taxCode', header: 'II', title: 'Indicador de IVA' }
};

// Columnas en el orden y con los nombres de la captura de SAP de cada cuenta
const COLUMNS: Record<AccountKind, ItemColumn[]> = {
  deudor: [
    COLUMN.status, COLUMN.company, COLUMN.account, COLUMN.reference, COLUMN.invoice,
    COLUMN.documentDate, COLUMN.dueDate, COLUMN.paymentMethod, COLUMN.specialIndicator,
    COLUMN.documentType, COLUMN.due, COLUMN.amount, COLUMN.documentNumber, COLUMN.text,
    COLUMN.clearingDocument, COLUMN.clearingDate, COLUMN.taxCode
  ],
  acreedor: [
    COLUMN.status, COLUMN.company, COLUMN.documentNumber, COLUMN.reference,
    { ...COLUMN.documentType, header: 'CLASE' },
    COLUMN.documentDate, COLUMN.dueDate, COLUMN.specialIndicator, COLUMN.paymentMethod, COLUMN.due,
    { ...COLUMN.amount, header: 'IMPORTE EN ML' },
    COLUMN.currency, COLUMN.documentCurrency, COLUMN.clearingDocument, COLUMN.text
  ]
};

// Lo que cambia entre la cuenta de un deudor y la de un acreedor, además de las columnas
const KIND_TEXT: Record<AccountKind, { group: string; holder: string; label: string; partner: string }> = {
  deudor: { group: 'DEUDORES', holder: 'Deudor', label: 'deudor', partner: 'cliente' },
  acreedor: { group: 'ACREEDORES', holder: 'Acreedor', label: 'acreedor', partner: 'proveedor' }
};

// Moneda de la sociedad
const CURRENCY = 'EUR';

// Sin endpoint todavía: la tabla sale vacía
const items: AccountItem[] = [];

// Sin €, como en SAP: es el importe en la moneda de la sociedad
const formatAmount = (value: number, privacyMode: boolean): string =>
  privacyMode
    ? '•••••'
    : new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

interface FinanceAccountItemsProps {
  kind: AccountKind;
  // Cliente o proveedor del maestro
  account: { id: string; name: string; city: string };
  privacyMode: boolean;
  onClose: () => void;
}

export const FinanceAccountItems = ({ kind, account, privacyMode, onClose }: FinanceAccountItemsProps) => {
  const [scope, setScope] = useState<ItemScope>('abiertas');
  const companyId = useSelector((state: RootState) => state.activeCompany.activeId) || '';

  const text = KIND_TEXT[kind];
  const columns = COLUMNS[kind];
  const amountIndex = columns.map(c => c.id).indexOf('amount');
  const rows = scope === 'abiertas' ? items.filter(i => i.status === 'abierta') : items;
  const total = rows.reduce((sum, i) => sum + i.amount, 0);
  const now = today();

  const renderCell = (id: ColumnId, item: AccountItem): React.ReactNode => {
    switch (id) {
      case 'status':
        return (
          <span className={`module-badge ${item.status === 'abierta' ? 'inactive' : 'paid'}`}>
            {item.status === 'abierta' ? 'Abierta' : 'Compensada'}
          </span>
        );
      case 'company': return companyId;
      case 'account': return account.id;
      case 'reference': return <span className="fixed-assets-view__code">{item.reference}</span>;
      case 'invoice': return item.invoice;
      case 'documentDate': return formatDate(item.documentDate);
      case 'dueDate': return formatDate(item.dueDate);
      case 'paymentMethod': return item.paymentMethod;
      case 'specialIndicator': return item.specialIndicator;
      case 'documentType': return item.documentType;
      case 'due':
        if (item.status !== 'abierta') return null;
        return item.dueDate < now
          ? <span className="module-badge overdue">Vencida</span>
          : <span className="module-badge pending">No vencida</span>;
      case 'amount': return formatAmount(item.amount, privacyMode);
      case 'currency': return CURRENCY;
      case 'documentCurrency': return item.documentCurrency;
      case 'documentNumber': return item.documentNumber;
      case 'text': return <span className="fixed-assets-view__name">{item.text}</span>;
      case 'clearingDocument': return item.clearingDocument;
      case 'clearingDate': return formatDate(item.clearingDate);
      case 'taxCode': return item.taxCode;
      default: return null;
    }
  };

  return (
    <>
      {/* Volver arriba a la izquierda, igual que en Contabilizar activos */}
      <div className="qa-back-bar">
        <div className="qa-back-bar-left">
          <button type="button" onClick={onClose} className="qa-back-btn">
            <ArrowLeft size={14} /> Volver
          </button>
          <div>
            <span className="qa-back-bar-category">{text.group}</span>
            <h2 className="qa-back-bar-title">Partidas de {text.label}</h2>
          </div>
        </div>
        <span className="qa-back-bar-hint">Partidas abiertas y compensadas por {text.partner}</span>
      </div>

      <div className="fin-doc">
        <div className="fin-doc__header">
          <div>
            <span className="fin-doc__tag">{text.group} • PARTIDAS</span>
            <h3 className="fin-doc__title"><ScrollText size={14} /> Cuenta de {text.label}</h3>
          </div>
        </div>

        <div className="fin-doc__grid fin-doc__grid--account">
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">{text.holder}</label>
            <input type="text" readOnly className="sidebar-form-input fin-doc__mono fin-doc__readonly" value={account.id} />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Sociedad</label>
            <input type="text" readOnly className="sidebar-form-input fin-doc__mono fin-doc__readonly" value={companyId} />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Nombre</label>
            <input type="text" readOnly className="sidebar-form-input fin-doc__readonly" value={account.name} />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Población</label>
            <input type="text" readOnly className="sidebar-form-input fin-doc__readonly" value={account.city || '—'} />
          </div>
        </div>

        <div className="fin-doc__grid">
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Partidas</label>
            <select
              className="sidebar-form-input"
              value={scope}
              onChange={(e) => setScope(e.target.value as ItemScope)}
            >
              <option value="abiertas">Abiertas</option>
              <option value="todas">Todas (con histórico)</option>
            </select>
          </div>
        </div>

        <div className="fixed-assets-view__table-wrapper">
          <table className="fixed-assets-view__table">
            <thead>
              <tr>
                {columns.map(col => (
                  <th key={col.id} className={col.right ? 'right' : undefined} title={col.title}>{col.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="module-panel-empty">
                    Pendiente de endpoint: todavía no se pueden consultar las partidas del {text.label}.
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map(item => (
                    <tr key={item.id} className="fixed-assets-view__table-row">
                      {columns.map(col => (
                        <td key={col.id} className={col.right ? 'right' : undefined}>{renderCell(col.id, item)}</td>
                      ))}
                    </tr>
                  ))}
                  <tr className="fixed-assets-view__table-total">
                    <td colSpan={amountIndex}>TOTAL ({rows.length})</td>
                    {columns.slice(amountIndex).map(col => (
                      <td key={col.id} className={col.right ? 'right' : undefined}>
                        {col.id === 'amount' ? formatAmount(total, privacyMode) : col.id === 'currency' ? CURRENCY : null}
                      </td>
                    ))}
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default FinanceAccountItems;
